/**
 * @fileoverview State and handlers for ProposalsListView.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { logger, sanitizeError } from "../../utils/logger.js";
import {
  getProposals,
  getCategories,
  handleProposalAction,
  handleRejectSubmit,
  handleToggleActive,
} from "../../logic/proposal/index.js";

export const useProposalsListView = (requirement) => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDisplayModal, setShowDisplayModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);
  const [acceptedExpanded, setAcceptedExpanded] = useState(true);
  const [rejectedExpanded, setRejectedExpanded] = useState(false);
  const [isActive, setIsActive] = useState(requirement?.activo || false);

  const fetchCategories = useCallback(async () => {
    const categoriesData = await getCategories(navigate);
    setCategories(categoriesData);
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const proposalsData = await getProposals(navigate);
      const filteredProposals = (proposalsData || []).filter(
        (proposal) => proposal.idRequerimiento === requirement._id,
      );
      setProposals(filteredProposals);
    } catch (error) {
      logger.error("Error fetching data:", { err: sanitizeError(error) });
    }
  }, [navigate, requirement._id]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!!token && token.trim() !== "undefined" && requirement) {
      fetchData();
      fetchCategories();
    } else {
      logger.info("Error trying to get the token to retrieve the users data.");
    }
  }, [requirement, fetchData, fetchCategories]);

  const onProposalAction = useCallback(
    async (proposalId, action) => {
      if (action === "star" || action === "unstar") {
        setProposals((currentProposals) =>
          currentProposals.map((proposal) =>
            proposal._id === proposalId
              ? { ...proposal, esDestacado: action === "star" }
              : proposal,
          ),
        );
      }

      await handleProposalAction(
        proposalId,
        action,
        proposals,
        navigate,
        setSelectedProposal,
        setShowRejectModal,
        fetchData,
      );
    },
    [fetchData, navigate, proposals],
  );

  const onRejectSubmit = useCallback(async () => {
    await handleRejectSubmit(fetchData, setShowSuccessMessage, setIsSlidingUp);
  }, [fetchData]);

  const onToggleActive = useCallback(async () => {
    const newActiveState = await handleToggleActive(
      requirement._id,
      isActive,
      navigate,
    );
    setIsActive(newActiveState);
  }, [requirement._id, isActive, navigate]);

  const handleViewProposal = useCallback((proposal) => {
    setSelectedProposal(proposal);
    setShowDisplayModal(true);
  }, []);

  const handleModalClose = useCallback(
    async (modalType) => {
      if (modalType === "reject") {
        setShowRejectModal(false);
      } else if (modalType === "display") {
        setShowDisplayModal(false);
      } else {
        // No modal to close for this type
      }
      setSelectedProposal(null);
      try {
        await fetchData();
      } catch (error) {
        logger.error("Error fetching data after modal close:", {
          err: sanitizeError(error),
        });
      }
    },
    [fetchData],
  );

  const toggleAcceptedExpanded = useCallback(() => {
    setAcceptedExpanded((expanded) => !expanded);
  }, []);

  const toggleRejectedExpanded = useCallback(() => {
    setRejectedExpanded((expanded) => !expanded);
  }, []);

  return {
    proposals,
    categories,
    showRejectModal,
    showDisplayModal,
    selectedProposal,
    showSuccessMessage,
    isSlidingUp,
    acceptedExpanded,
    rejectedExpanded,
    isActive,
    onProposalAction,
    onRejectSubmit,
    onToggleActive,
    handleViewProposal,
    handleModalClose,
    toggleAcceptedExpanded,
    toggleRejectedExpanded,
  };
};
